import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { AppDispatch, RootState } from '@reducers/index';
import { fetchLogin } from '@reducers/authSlice';
import { useNavigate } from 'react-router-dom';
import { LogInType, ILogInForm } from '@models/auth/authModel';
import { axiosDummyInstance } from '@utils/axios/axiosInstance';
import { emailChecker, passwordChecker } from '@utils/common/validators';
import PasswordFindPopup from '@components/template/account/login/passwordFindPopup';

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [isPasswordHide, setIsPasswordHide] = useState(true);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<ILogInForm>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // submit handler
  const onSubmit = useCallback(
    async (formObj: ILogInForm) => {
      console.log('formObj: ', formObj);
      try {
        // 로긴 요청처리하기
        const logInResult = await dispatch(fetchLogin({ ...formObj, type: LogInType.aix })).unwrap();
        console.log('logInResult: ', logInResult);
        // 로긴 완료 시 등록 페이지로 넘기기 ()
        // @ts-ignore
        if (logInResult.message === 'OK') {
          return navigate('/manager/service/appManager');
        } else if (logInResult.message === 'WRONG_PASSWORD') {
          // password 부분 에러 처리
          setError('password', { message: 'password is wrong' }, { shouldFocus: true });
        } else if (logInResult.message === 'WRONG_EMAIL') {
          // email 부분 에러 처리
          setError('email', { message: 'email does not exist' }, { shouldFocus: true });
        }
      } catch (error) {
        console.log('error: ', error);
      }
    },
    [dispatch, fetchLogin, navigate, setError]
  );

  // social login
  const onClickSNSLogin = useCallback(
    async (type: LogInType) => {
      const logInResult = await dispatch(fetchLogin({ type })).unwrap();
      console.log('logInResult: ', logInResult);
      // @ts-ignore
      if (logInResult.message === 'OK') {
        return navigate('/manager/service/appManager');
        // http://localhost:3000/appManager/myApps   성공 시 일로 연결하기
      } else if (logInResult.message === 'WRONG_PASSWORD') {
        // password 부분 에러 처리
        setError('password', { message: 'password is wrong' }, { shouldFocus: true });
      } else if (logInResult.message === 'WRONG_EMAIL') {
        // email 부분 에러 처리
        setError('email', { message: 'email does not exist' }, { shouldFocus: true });
      }
    },
    [dispatch, fetchLogin]
  );

  // register 버튼
  const onClickRegister = useCallback(() => {
    navigate('/signup');
  }, [navigate]);

  // 비번 보이기 처리
  const onChangePasswordHide = useCallback(() => {
    setIsPasswordHide((prev) => !prev);
  }, []);

  // 팝업창 닫기
  const popupHandler = useCallback((isOpen) => {
    setIsPopupOpen(isOpen);
  }, []);

  return (
    <div className="other-layout login">
      <div className="main-content">
        <header className="header">
          <div className="header-inner">
            <h1 className="header-logo">
              <img src="/images/logo-axo-app.png" alt="axo" />
            </h1>

            <label className="select type-1">
              <select>
                <option>ENG</option>
                <option>JP</option>
                <option>KOR</option>
              </select>
            </label>
          </div>
        </header>
        <div className="container">
          <div>
            <div className="manager">
              <h2 className="manager-tit">axo Account Login</h2>
              <p className="manager-dsc">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-ipt">
                <div className="form-ipt-tit">E-Mail</div>
                <label className="ipt">
                  <input
                    type="text"
                    placeholder="Enter your email address"
                    className={`${errors?.email ? 'error' : ''}`}
                    {...register('email', {
                      required: 'email is required',
                      validate: {
                        emailformCheck: (value) => (emailChecker(value) ? true : 'email form is invalid'),
                      },
                    })}
                  />
                  {errors?.email && <span className="error-txt">{errors.email?.message}</span>}
                </label>

                <div className="form-ipt-tit">Password</div>
                <div className="ipt pw">
                  <label>
                    <input
                      type={`${isPasswordHide ? 'password' : 'text'}`}
                      placeholder="비밀번호 8~20글자, 알파벳, 숫자 혼합"
                      className={`${errors?.password ? 'error' : ''}`}
                      {...register('password', {
                        required: 'password is required',
                        validate: {
                          passwordFormCheck: (value) => (passwordChecker(value) ? true : 'password form is invalid'),
                        },
                      })}
                    />
                    {errors?.password && <span className="error-txt">{errors.password?.message}</span>}
                  </label>
                  <div className="pw-show-hide">
                    <input
                      type="checkbox"
                      className="ipt-check eyes"
                      id="checked-1"
                      checked={isPasswordHide}
                      onChange={onChangePasswordHide}
                    />
                    <label htmlFor="checked-1">
                      <span className="blind">감추기</span>
                    </label>
                  </div>
                </div>
                <div className="form-help">
                  <div className="save-checked">
                    <input type="checkbox" className="ipt-check basic" id="checked-5" />
                    <label htmlFor="checked-5">Users sharing positions</label>
                  </div>
                  {/* TODO: 이부분 로직 대표님께 물어보기 */}
                  <button type="button" className="forgot-pw" onClick={() => popupHandler(true)}>
                    Forgot password?
                  </button>
                </div>
              </div>

              <div className="login-btn">
                <button type="submit" className="btn-b pri">
                  Log in
                </button>
              </div>
              <p className="register-now">
                Don't you have a registered account?{' '}
                <button type="button" onClick={onClickRegister}>
                  Register now
                </button>
              </p>

              <div className="btn-acccount">
                <button
                  type="button"
                  className="btn-acccount-facebook"
                  onClick={() => onClickSNSLogin(LogInType.facebook)}
                >
                  Continue wite Facebook
                </button>
                <button type="button" className="btn-acccount-google" onClick={() => onClickSNSLogin(LogInType.google)}>
                  Continue wite Google
                </button>
              </div>
            </form>
          </div>
          {/* 여기서 패스워드 팝업처리하기 */}
          {isPopupOpen && <PasswordFindPopup popupHandler={popupHandler} />}
        </div>
      </div>
    </div>
  );
};

export default Login;
